import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { userService } from '../services/userService';
import { useLoading } from '../contexts/LoadingContext';
import { reportHostIssue } from '../utils/hostIssues/hostIssueReporter';
import { EntitlementsProvider, isTeamMember } from '../books/entitlements';
import {
  DEFAULT_RETRY,
  runWithRetry,
} from '../data/repositories/documentWriteQueue';
import AccessCheckUnreachable from './AccessCheckUnreachable';

// The sign-in's claims as the CACHED token carries them — the token the
// Firestore rules see. A failed read (offline with an expired token) is no
// claims: it can only withhold a membership, never grant one.
const readClaims = (user) =>
  user
    .getIdTokenResult()
    .then((result) => result.claims)
    .catch((error) => {
      console.warn('[PrivateRoute] could not read the sign-in claims', error);
      return {};
    });

/** Where the gate sends this visitor, or null when it lets them in / waits. */
function redirectTarget({
  loading,
  isAuthenticatingFromIOS,
  user,
  hasAccess,
  isHomeowner,
  partwayThroughPreview,
}) {
  if (loading || isAuthenticatingFromIOS) return null;
  if (!user) return '/login';
  if (hasAccess !== false) return null;
  if (isHomeowner) return '/my-designs';
  return partwayThroughPreview ? '/preview' : '/waitlist';
}

// Access gate. Allows in:
//   - Founding contractors with an active subscription (subscriptionStatus === 'active')
//   - Grandfathered legacy users (hasPreviewAccess === true)
//   - Members of a books organization (the `orgId` claim — phase 3d §3: a
//     teammate who joined by invite has neither of the above)
//
// A member passes on EVERY route, the design app included, and that is the
// rule, not an oversight (the user's call, 2026-09-26): the design side has
// no paywall — it is entered with a preview code OR an invitation (a
// project's share button), and the founders subscription above is an unused
// prototype. A team invite from an owner IS an invitation. The one way this
// could skip the waitlist — someone creating an organization for themselves
// — is closed where organizations are made: only an admin-marked contractor
// can create one (`createBooksOrganization`). Do not narrow members to the
// desk's routes here. reference-docs/contractor-books/overview.md, "A member
// passes PrivateRoute on EVERY route".
// Bounces:
//   - Unauthenticated users → /login
//   - Partway-through preview users (started a Stripe sub but haven't
//     paid yet) → /preview, where the auth-aware entry resumes them
//     on the payment screen
//   - Everyone else → /waitlist
//
// The "Simple OR" logic — slice 6 of the contractor preview rollout.
// Future-proofs grandfathered → paid conversion: a grandfathered user
// who starts a Stripe flow but bails won't suddenly lose access,
// because their hasPreviewAccess === true keeps them in.
function PrivateRoute({ children, user, isAuthenticatingFromIOS }) {
  const [loading, setLoadingState] = useState(true);
  const [hasAccess, setHasAccess] = useState(null);
  const [partwayThroughPreview, setPartwayThroughPreview] = useState(false);
  // A homeowner who signed up from a shared design link. They have an account
  // but no contractor app to be let into — bouncing them to /waitlist would
  // sit them in front of a page about becoming a contractor, wondering where
  // the design they just saved went.
  const [isHomeowner, setIsHomeowner] = useState(false);
  // The doc this gate already reads is also who the account IS: the desk's
  // one entitlement check (`books/entitlements.js`) reads it from here.
  const [userDoc, setUserDoc] = useState(null);
  // …and the sign-in's claims say which organization they belong to.
  const [claims, setClaims] = useState(null);
  // The read never got through, so whether they have access is unknown —
  // not "no". `checks` counts Try again presses, to run the check again.
  const [unreachable, setUnreachable] = useState(false);
  const [checks, setChecks] = useState(0);
  const { showLoading, hideLoading } = useLoading();

  useEffect(() => {
    const checkAccess = async () => {
      if (!user) {
        setLoadingState(false);
        return;
      }

      showLoading();
      try {
        // A dropped connection fails this read with `unavailable` ("the
        // client is offline"); retried as the write queue retries a save,
        // so a blip costs a second, not the account.
        const [userData, tokenClaims] = await runWithRetry(
          () => Promise.all([userService.getUser(user.uid), readClaims(user)]),
          DEFAULT_RETRY
        );
        setClaims(tokenClaims);
        if (userData) {
          setUserDoc(userData);
          setIsHomeowner(userData.accountKind === 'homeowner');
          const isActive = userData.subscriptionStatus === 'active';
          const isGrandfathered = userData.hasPreviewAccess === true;
          // a teammate: on every route — see the gate's comment above
          setHasAccess(
            isActive || isGrandfathered || isTeamMember(tokenClaims)
          );
          // Partway-through: signed in and started a subscription but
          // never landed at 'active'. Send them back to /preview so
          // the auth-aware entry resumes them at payment.
          setPartwayThroughPreview(
            !isActive &&
              !isGrandfathered &&
              (Boolean(userData.subscriptionStatus) ||
                Boolean(userData.stripeSubscriptionId))
          );
        } else {
          // User document doesn't exist - shouldn't happen but handle it
          setHasAccess(false);
        }
      } catch (error) {
        console.error('Error checking user access:', error);
        // Only an answer from the server can say "no access". A read that
        // never reached it used to land a paying customer on /waitlist.
        if (DEFAULT_RETRY.isTransient(error)) setUnreachable(true);
        else setHasAccess(false);
      } finally {
        setLoadingState(false);
        hideLoading();
      }
    };

    checkAccess();
  }, [user, showLoading, hideLoading, checks]);

  // Try again checks afresh, and nothing is let through while it runs
  const checkAgain = () => {
    setLoadingState(true);
    setUnreachable(false);
    setChecks((n) => n + 1);
  };

  const redirect = redirectTarget({
    loading,
    isAuthenticatingFromIOS,
    user,
    hasAccess,
    isHomeowner,
    partwayThroughPreview,
  });

  // Inside the iOS app every page is opened already signed in, so a bounce
  // off a private page means the WebView's session did not hold. Silent to
  // the user's eye ("it logged me out"), so it is reported explicitly; a
  // no-op in a browser.
  useEffect(() => {
    if (!redirect) return;
    reportHostIssue(user ? 'ios-no-access' : 'ios-signed-out', {
      message: `private route redirected to ${redirect}`,
      context: { redirect, signedIn: Boolean(user) },
    });
  }, [redirect, user]);

  if (loading || isAuthenticatingFromIOS) {
    return null; // LoadingProvider will show the loading screen
  }

  if (unreachable && user) {
    return <AccessCheckUnreachable onRetry={checkAgain} />;
  }

  if (redirect) return <Navigate to={redirect} />;

  return (
    <EntitlementsProvider userDoc={userDoc} claims={claims}>
      {children}
    </EntitlementsProvider>
  );
}

export default PrivateRoute;
